import PublicApiService from './PublicApiService';
import ProtectedApiService from './ProtectedApiService';

export interface UserProfile {
    username: string;
    appUserId?: string;
    handle?: string;
    bio?: string;
    pronouns?: string;
    location?: string;
    email?: string;
}

export interface UserAvatar {
    handle: string;
    bio: string | null;
    pronouns: string | null;
    location: string | null;
}

export interface AvatarData {
    handle?: string;
    bio?: string;
    pronouns?: string;
    location?: string;
}

/**
 * Service for user-related operations
 * Uses PublicApiService for public user data and ProtectedApiService for user management
 */
class UserService {
    private publicApi: PublicApiService;
    private protectedApi: ProtectedApiService;

    constructor() {
        this.publicApi = new PublicApiService();
        this.protectedApi = new ProtectedApiService();
    }

    // Public user operations (no authentication required)

    /**
     * Get a user's public profile
     */
    async getUserProfile(username: string): Promise<UserProfile> {
        const response = await this.publicApi.getRequest(`users/${username}/profile`);
        
        // Map backend response fields to frontend interface
        // Backend returns 'userName' but frontend expects 'username'
        return {
            username: response.userName || response.username || username,
            appUserId: response.appUserId,
            handle: response.handle,
            bio: response.bio,
            pronouns: response.pronouns,
            location: response.location,
            email: response.email
        };
    }

    /**
     * Get a user's public avatar information
     */
    async getUserAvatar(username: string): Promise<UserAvatar> {
        return this.publicApi.getRequest(`users/${username}/profile/avatar`);
    }

    /**
     * Check if a username is available for registration
     */
    async checkUsernameAvailability(username: string): Promise<{available: boolean, message?: string}> {
        try {
            return await this.publicApi.getRequest(`users/username-availability/${encodeURIComponent(username)}`);
        } catch (error: any) {
            if (error.response?.status === 404) {
                // Username not found means it's available
                return { available: true };
            }
            throw error;
        }
    }

    /**
     * Get a user's public kandis
     */
    async getUserKandis(username: string): Promise<any[]> {
        return this.publicApi.getRequest(`users/${username}/kandis`);
    }

    // Protected user operations (authentication required)

    /**
     * Update a user's profile (authenticated operation)
     */
    async updateUserProfile(username: string, data: Partial<UserProfile>): Promise<UserProfile> {
        return this.protectedApi.putRequest(`users/${username}/profile`, data);
    }

    /**
     * Set a user's avatar information (authenticated operation)
     */
    async setUserAvatar(username: string, avatarData: AvatarData): Promise<void> {
        try {
            const result = await this.protectedApi.putRequest(`users/${username}/profile/avatar`, avatarData);
            console.log('✅ UserService.setUserAvatar completed successfully:', result);
            return result;
        } catch (error) {
            console.error('❌ UserService.setUserAvatar failed:', error);
            throw error;
        }
    }
}

export default UserService;
